/**
 * Onglet Encaissements — Module Wave
 * -----------------------------------
 * Workflow strictement MANUEL (aucun auto-match par décision produit) :
 *   1. Section "Merchant Payments" : règlements clients à rattacher à une facture
 *   2. Section "Agent Transactions" : versements à classer (client / dépôt / interne)
 * Réservé aux rôles admin + finance.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ArrowDownRight, Building2, CheckCircle2, ChevronDown, ChevronRight, Coins, History, Loader2,
  Search, Send, ShieldAlert, Sparkles, UserCheck, Wallet, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { BulkActionBar, confirmBulk } from '../common/BulkActionBar';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fxof = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `${Math.round(v).toLocaleString('fr-FR')} XOF`;
};
const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(v); }
};

const CATEGORY_LABELS = {
  client_payment: { label: 'Règlement client', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cash_deposit: { label: 'Dépôt espèces', color: 'bg-sky-100 text-sky-800 border-sky-200' },
  internal_transfer: { label: 'Virement interne', color: 'bg-violet-100 text-violet-800 border-violet-200' },
  unknown: { label: 'Non identifié', color: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
};

// ────────────────────────────────────────────────────────────────────
// SOUS-COMPOSANT : Historique des paiements pour une facture
// ────────────────────────────────────────────────────────────────────
function InvoicePaymentsHistory({ sourceType, invoiceId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data } = await axios.get(`${API}/wave/invoice-payments/${sourceType}/${invoiceId}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Impossible de charger les paiements');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sourceType, invoiceId]);

  if (loading) {
    return (
      <div className="pl-8 py-2 text-[11px] text-zinc-500">
        <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Chargement des paiements...
      </div>
    );
  }
  if (error) return <div className="pl-8 py-2 text-[11px] text-rose-600">{error}</div>;
  if (!data) return null;

  const payments = data.payments || [];
  if (payments.length === 0) {
    return (
      <div className="pl-8 py-2 text-[11px] text-zinc-500 italic">
        Aucun paiement encore enregistré sur cette facture.
      </div>
    );
  }

  return (
    <div className="pl-8 pr-3 py-2 space-y-1 bg-white/60" data-testid="wave-link-payment-history">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold mb-1 flex items-center gap-1">
        <History className="h-3 w-3" /> Historique des paiements ({payments.length})
      </div>
      {payments.map((p, i) => (
        <div key={p.id || i}
          className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${
            p.voided ? 'bg-rose-50/60 line-through text-rose-500' : 'bg-emerald-50/40'
          }`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
            p.source === 'wave' ? 'bg-emerald-500' : p.source === 'bank' ? 'bg-blue-500' : 'bg-zinc-400'
          }`} />
          <span className="text-zinc-600 font-mono min-w-[85px]">{fmtDate(p.date).split(' ')[0]}</span>
          <span className="font-semibold text-zinc-800">{fxof(p.amount)}</span>
          <span className="text-zinc-500 text-[10px] uppercase">{p.method || '—'}</span>
          {p.reference && (
            <span className="text-zinc-400 font-mono text-[10px] truncate max-w-[140px]" title={p.reference}>
              {p.reference}
            </span>
          )}
          {p.source && (
            <span className={`ml-auto text-[9px] px-1 py-0.5 rounded ${
              p.source === 'wave' ? 'bg-emerald-100 text-emerald-700' :
              p.source === 'bank' ? 'bg-blue-100 text-blue-700' :
              'bg-zinc-100 text-zinc-600'
            }`}>
              {p.source}
            </span>
          )}
          {p.voided && <span className="text-[9px] text-rose-600 font-semibold">ANNULÉ</span>}
        </div>
      ))}
      <div className="flex items-center justify-between pt-1 border-t border-zinc-200 mt-1 text-[11px]">
        <span className="text-zinc-500">Total encaissé</span>
        <span className="font-semibold text-emerald-700">
          {fxof(data.amountPaid)} / {fxof(data.invoiceAmount)}
        </span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// MODAL : Lier merchant à facture(s) — Recherche unifiée Contrat + Opération spot
// ────────────────────────────────────────────────────────────────────
function LinkInvoiceModal({ open, onClose, tx, onDone }) {
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includePaid, setIncludePaid] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // all | contract | operation
  const [allocations, setAllocations] = useState([]);
  const [reason, setReason] = useState('');
  const [notifyClient, setNotifyClient] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedInvoices, setExpandedInvoices] = useState(new Set()); // IDs of expanded payment histories

  const toggleExpand = (id) => {
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!open) {
      setQuery(''); setCandidates([]); setAllocations([]);
      setReason(''); setNotifyClient(true); setIncludePaid(false); setTypeFilter('all');
      setExpandedInvoices(new Set());
    }
  }, [open]);

  const searchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/wave/searchable-invoices`, {
        params: { q: query.trim(), includePaid, type: typeFilter, limit: 30 },
      });
      setCandidates(data?.results || []);
    } catch (e) {
      toast({
        title: 'Recherche impossible',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally { setLoading(false); }
  }, [query, includePaid, typeFilter]);

  // Charge initial (sans query) + debounce sur query
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(searchInvoices, 250);
    return () => clearTimeout(t);
  }, [open, searchInvoices]);

  const addAllocation = (inv) => {
    if (allocations.some(a => a.invoiceId === inv.id)) return;
    const remaining = (tx?.amountSent || 0) - allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
    const proposed = Math.min(inv.amountDue || 0, Math.max(0, remaining));
    setAllocations([...allocations, {
      invoiceId: inv.id,
      sourceType: inv.sourceType,
      invoiceNumber: inv.invoiceNumber,
      companyName: inv.companyName,
      operationNumber: inv.operationNumber,
      amount: proposed,
      amountDue: inv.amountDue || 0,
    }]);
  };

  const updateAmount = (idx, val) => {
    const nv = [...allocations];
    nv[idx].amount = Math.max(0, Math.min(Number(val) || 0, nv[idx].amountDue));
    setAllocations(nv);
  };

  const removeAllocation = (idx) => setAllocations(allocations.filter((_, i) => i !== idx));

  const totalAllocated = allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
  const overAllocated = totalAllocated > (tx?.amountSent || 0);
  const remainingAfter = (tx?.amountSent || 0) - totalAllocated;

  const submit = async () => {
    if (!allocations.length) { toast({ title: 'Ajoutez au moins une facture', variant: 'destructive' }); return; }
    if (overAllocated) { toast({ title: 'Somme des allocations > montant reçu', variant: 'destructive' }); return; }
    if (reason.trim().length < 3) { toast({ title: 'Motif requis (min 3 car.)', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/wave/transactions/${tx.id}/link-invoices`, {
        allocations: allocations.map(a => ({
          invoiceId: a.invoiceId,
          amount: Math.round(Number(a.amount)),
          sourceType: a.sourceType || 'contract',
        })),
        reason: reason.trim(), notifyClient,
      });
      toast({
        title: 'Lien créé',
        description: `${data.payments?.length || 0} paiement(s) enregistré(s) — ${fxof(data.totalAllocated)}`,
      });
      onDone?.(); onClose();
    } catch (e) {
      toast({
        title: 'Échec liaison',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally { setSubmitting(false); }
  };

  if (!tx) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wave-link-invoice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-emerald-600" /> Lier ce paiement à une facture
          </DialogTitle>
          <DialogDescription>
            Wave&nbsp;: <strong>{tx.waveRef}</strong> · Contrepartie&nbsp;: <strong>{tx.beneficiaryName || '—'}</strong> · Montant reçu&nbsp;: <strong>{fxof(tx.amountSent)}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Barre de recherche */}
          <div>
            <Label className="text-xs font-medium">Rechercher (n° opération, n° facture, ou société)</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-zinc-400" />
              <Input
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="ex: BAO-OP-2026-… · FACT-2026-0122 · INV-2026 · SOGENA"
                className="pl-7 h-9 text-sm"
                data-testid="wave-link-invoice-search"
                autoFocus
              />
            </div>
          </div>

          {/* Filtres */}
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-zinc-500">Type&nbsp;:</span>
              {[
                { v: 'all', label: 'Toutes' },
                { v: 'contract', label: '📄 Contrat' },
                { v: 'operation', label: '⚡ Opération' },
              ].map(o => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setTypeFilter(o.v)}
                  className={`px-2 py-0.5 rounded border transition ${
                    typeFilter === o.v
                      ? 'bg-emerald-600 border-emerald-600 text-white'
                      : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                  }`}
                  data-testid={`wave-link-type-${o.v}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1 cursor-pointer ml-auto">
              <input
                type="checkbox" checked={includePaid}
                onChange={(e) => setIncludePaid(e.target.checked)}
                data-testid="wave-link-include-paid"
              />
              <span className="text-zinc-600">Inclure factures soldées</span>
            </label>
          </div>

          {/* Résultats */}
          {loading && (
            <div className="text-xs text-zinc-500 py-4 text-center">
              <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Recherche...
            </div>
          )}

          {!loading && candidates.length === 0 && (
            <div className="text-xs text-zinc-400 py-6 text-center border border-dashed border-zinc-200 rounded-md">
              Aucune facture. Essayez un autre critère ou activez « Inclure factures soldées ».
            </div>
          )}

          {!loading && candidates.length > 0 && (
            <div className="border border-zinc-200 rounded-md max-h-[350px] overflow-y-auto divide-y">
              {candidates.map(inv => {
                const already = allocations.some(a => a.invoiceId === inv.id);
                const isSpot = inv.sourceType === 'operation';
                const isExpanded = expandedInvoices.has(inv.id);
                const hasHistory = (inv.amountPaid || 0) > 0;
                return (
                  <div key={`${inv.sourceType}-${inv.id}`}
                    className={`hover:bg-zinc-50 transition ${already ? 'opacity-40' : ''} ${inv.isOverdue ? 'border-l-4 border-l-rose-500' : ''}`}
                    data-testid={`wave-link-candidate-${inv.id}`}>
                    <div className="px-3 py-2.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 space-y-1">
                        {/* Header : badge + numéro + statut */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            isSpot ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                   : 'bg-blue-100 text-blue-700 border border-blue-200'
                          }`}>
                            {isSpot ? '⚡ OPÉRATION' : '📄 CONTRAT'}
                          </span>
                          <span className="font-semibold text-sm">{inv.invoiceNumber}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                            inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            inv.isOverdue ? 'bg-rose-50 text-rose-700 border-rose-200' :
                            'bg-zinc-50 text-zinc-700 border-zinc-200'
                          }`}>
                            {inv.status}
                            {inv.isOverdue && ` · +${inv.daysOverdue}j`}
                          </span>
                        </div>

                        {/* Société */}
                        <div className="text-xs text-zinc-700 font-medium">{inv.companyName}</div>

                        {/* Opération (spot only) */}
                        {isSpot && inv.operationNumber && (
                          <div className="text-[11px] text-zinc-500">
                            Opération&nbsp;: <span className="font-mono">{inv.operationNumber}</span>
                            {inv.route && <> · {inv.route}</>}
                          </div>
                        )}

                        {/* Montants */}
                        <div className="flex items-center gap-3 text-[11px] pt-0.5">
                          <span className="text-zinc-500">
                            TTC&nbsp;: <span className="font-medium text-zinc-700">{fxof(inv.montantTotalTTC)}</span>
                          </span>
                          {inv.amountPaid > 0 && (
                            <span className="text-zinc-500">
                              Payé&nbsp;: <span className="text-emerald-700">{fxof(inv.amountPaid)}</span>
                            </span>
                          )}
                          <span className="text-zinc-500">
                            Reste dû&nbsp;: <strong className={inv.amountDue > 0 ? 'text-rose-700' : 'text-emerald-700'}>{fxof(inv.amountDue)}</strong>
                          </span>
                          {hasHistory && (
                            <button
                              type="button"
                              onClick={() => toggleExpand(inv.id)}
                              className="ml-auto flex items-center gap-1 text-[10px] text-emerald-700 hover:text-emerald-900 hover:underline"
                              data-testid={`wave-link-toggle-history-${inv.id}`}
                            >
                              <History className="h-3 w-3" />
                              {isExpanded ? 'Masquer' : 'Voir'} paiements
                              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                            </button>
                          )}
                        </div>

                        {/* Dates */}
                        <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                          {inv.issuedAt && <span>Émise le {fmtDate(inv.issuedAt).split(' ')[0]}</span>}
                          {inv.dueDate && <span>· Échéance {fmtDate(inv.dueDate).split(' ')[0]}</span>}
                        </div>
                      </div>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addAllocation(inv)}
                        disabled={already || inv.amountDue <= 0}
                        data-testid={`wave-link-add-${inv.id}`}
                        className="shrink-0"
                      >
                        {already ? '✓ Ajoutée' : 'Lier →'}
                      </Button>
                    </div>
                    </div>
                    {/* Historique paiements — expanded */}
                    {isExpanded && (
                      <div className="border-t border-zinc-100 bg-zinc-50/40">
                        <InvoicePaymentsHistory sourceType={inv.sourceType} invoiceId={inv.id} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Allocations sélectionnées */}
          {allocations.length > 0 && (
            <div className="border border-emerald-200 rounded-md bg-emerald-50/50 p-2 space-y-1.5">
              <div className="text-xs font-medium text-emerald-800 mb-1">Allocations ({allocations.length})</div>
              {allocations.map((a, idx) => (
                <div key={a.invoiceId} className="flex items-center gap-2 text-xs">
                  <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${
                    a.sourceType === 'operation' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {a.sourceType === 'operation' ? '⚡' : '📄'}
                  </span>
                  <div className="flex-1 min-w-0 truncate">
                    {a.invoiceNumber} · {a.companyName}
                    {a.operationNumber && <span className="text-zinc-500"> ({a.operationNumber})</span>}
                    <span className="text-zinc-500"> · dû {fxof(a.amountDue)}</span>
                  </div>
                  <Input type="number" value={a.amount} onChange={(e) => updateAmount(idx, e.target.value)}
                    className="h-7 w-28 text-right text-xs" min={0} max={a.amountDue}
                    data-testid={`wave-link-alloc-amount-${idx}`} />
                  <span className="text-zinc-500">XOF</span>
                  <Button size="icon" variant="ghost" onClick={() => removeAllocation(idx)} className="h-6 w-6">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <div className={`flex items-center justify-between pt-1 border-t border-emerald-200 mt-1 text-xs ${overAllocated ? 'text-rose-700' : 'text-emerald-800'}`}>
                <span>Total alloué</span>
                <strong>{fxof(totalAllocated)} / {fxof(tx.amountSent)}</strong>
              </div>
              {remainingAfter > 0 && !overAllocated && (
                <div className="text-[11px] text-amber-700">
                  Reste {fxof(remainingAfter)} non affecté — restera « non identifié » sur la tx.
                </div>
              )}
              {overAllocated && <div className="text-[11px] text-rose-700 font-medium">Dépassement : réduisez une allocation.</div>}
            </div>
          )}

          <div>
            <Label className="text-xs font-medium">Motif du rattachement *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Règlement identifié par appel client / cohérence montant + nom / réf transmise..."
              className="min-h-[60px] text-xs mt-1" data-testid="wave-link-reason" />
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={notifyClient} onChange={(e) => setNotifyClient(e.target.checked)} data-testid="wave-link-notify" />
            <span>Notifier le client (in-app)</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button onClick={submit} disabled={submitting || !allocations.length || overAllocated || reason.trim().length < 3}
            className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wave-link-submit">
            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Confirmer le rattachement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// MODAL : Motif court pour classification (cash_deposit / internal / unidentified)
// ────────────────────────────────────────────────────────────────────
function ReasonModal({ open, onClose, title, description, minChars = 10, onSubmit, submitting }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (!open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="wave-reason-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={`Motif obligatoire (min ${minChars} car.)`}
            className="min-h-[70px] text-xs" data-testid="wave-reason-input" />
          <div className="text-[11px] text-zinc-500 text-right mt-1">
            {reason.trim().length} / {minChars}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button onClick={() => onSubmit(reason.trim())} disabled={submitting || reason.trim().length < minChars}
            data-testid="wave-reason-submit">
            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// COMPONENT PRINCIPAL
// ────────────────────────────────────────────────────────────────────
export function WaveInflowsPanel({ period }) {
  const [kpis, setKpis] = useState(null);
  const [merchantList, setMerchantList] = useState({ items: [], total: 0 });
  const [agentList, setAgentList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  // modals
  const [linkTx, setLinkTx] = useState(null);
  const [reasonModal, setReasonModal] = useState(null); // {tx, action, category}

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = period ? { period } : {};
      const [k, m, a] = await Promise.all([
        axios.get(`${API}/wave/inflows/kpis`, { params }),
        axios.get(`${API}/wave/inflows`, { params: { ...params, tx_type: 'merchant_payment', limit: 100 } }),
        axios.get(`${API}/wave/inflows`, { params: { ...params, tx_type: 'agent_transaction', limit: 100 } }),
      ]);
      setKpis(k.data); setMerchantList(m.data); setAgentList(a.data);
      // Diffuse un event pour rafraîchir les badges sidebar/onglets partout
      try { window.dispatchEvent(new CustomEvent('wave-pending-refresh')); } catch { /* noop */ }
    } catch (e) {
      toast({ title: 'Erreur de chargement', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submitReason = async (reasonText) => {
    if (!reasonModal) return;
    const { tx, action, category } = reasonModal;
    try {
      setReasonModal({ ...reasonModal, submitting: true });
      if (action === 'mark_unidentified') {
        await axios.post(`${API}/wave/transactions/${tx.id}/mark-unidentified`, { reason: reasonText });
      } else if (action === 'classify') {
        await axios.post(`${API}/wave/transactions/${tx.id}/classify`, { category, reason: reasonText });
      }
      toast({ title: 'Enregistré' });
      setReasonModal(null);
      fetchAll();
    } catch (e) {
      toast({ title: 'Échec', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      setReasonModal({ ...reasonModal, submitting: false });
    }
  };

  // Filtre : afficher les unlinked/unclassified en priorité (tri)
  const merchantSorted = useMemo(() => {
    const items = merchantList.items || [];
    return [...items].sort((a, b) => {
      const aLinked = (a.linkedInvoiceIds?.length || 0) > 0 || a.depositCategory === 'unknown';
      const bLinked = (b.linkedInvoiceIds?.length || 0) > 0 || b.depositCategory === 'unknown';
      if (aLinked !== bLinked) return aLinked ? 1 : -1;
      return new Date(b.date || 0) - new Date(a.date || 0);
    });
  }, [merchantList]);

  // Sélection multiple (25/09/2026) — un seul motif saisi pour tout le lot
  const [agentSelection, setAgentSelection] = useState([]);
  const [merchantSelection, setMerchantSelection] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkModal, setBulkModal] = useState(null); // {kind, ids, category, label}

  const unclassifiedAgentIds = useMemo(
    () => (agentList.items || [])
      .filter(t => !t.depositCategory || t.depositCategory === 'unknown')
      .map(t => t.id),
    [agentList],
  );

  // Merchants « à traiter » : ni rattachés à une facture, ni déjà marqués non identifiés
  const pendingMerchantIds = useMemo(
    () => (merchantList.items || [])
      .filter(t => (t.linkedInvoiceIds?.length || 0) === 0 && t.depositCategory !== 'unknown')
      .map(t => t.id),
    [merchantList],
  );

  const openBulk = (cfg) => {
    if (!cfg.ids.length) return;
    if (!confirmBulk(cfg.ids.length, `${cfg.label} appliqué`)) return;
    setBulkModal(cfg);
  };

  const submitBulk = async (reasonText) => {
    if (!bulkModal) return;
    const { kind, ids, category, label } = bulkModal;
    setBulkBusy(true);
    try {
      const url = kind === 'classify'
        ? `${API}/wave/transactions/classify/bulk`
        : `${API}/wave/transactions/mark-unidentified/bulk`;
      const body = kind === 'classify'
        ? { transactionIds: ids, category, reason: reasonText }
        : { transactionIds: ids, reason: reasonText };
      const { data } = await axios.post(url, body);
      const failed = (data.failed || []).length;
      toast({
        title: `${data.updated} ligne(s) traitée(s) → ${label}`,
        description: failed ? `${failed} ligne(s) en échec` : undefined,
        variant: failed ? 'destructive' : undefined,
      });
      setBulkModal(null);
      if (kind === 'classify') setAgentSelection([]); else setMerchantSelection([]);
      fetchAll();
    } catch (e) {
      toast({ title: 'Action en lot impossible',
        description: e?.response?.data?.detail, variant: 'destructive' });
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkClassify = (category, actionLabel) =>
    openBulk({ kind: 'classify', ids: agentSelection, category, label: actionLabel });


  const agentSorted = useMemo(() => {
    const items = agentList.items || [];
    return [...items].sort((a, b) => {
      const aClassified = !!a.depositCategory && a.depositCategory !== 'unknown';
      const bClassified = !!b.depositCategory && b.depositCategory !== 'unknown';
      if (aClassified !== bClassified) return aClassified ? 1 : -1;
      return new Date(b.date || 0) - new Date(a.date || 0);
    });
  }, [agentList]);

  return (
    <div className="space-y-5" data-testid="wave-inflows-panel">
      {/* KPI Bar */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="wave-inflows-kpis">
          <div className="bg-white border border-zinc-200 rounded-lg p-3">
            <div className="text-[11px] text-zinc-500 uppercase tracking-wide flex items-center gap-1">
              <Building2 className="h-3 w-3" /> Encaissements Merchants
            </div>
            <div className="text-lg font-bold text-emerald-700">{fxof(kpis.merchant?.total?.amount)}</div>
            <div className="text-[11px] text-zinc-500">{kpis.merchant?.total?.count || 0} tx · Taux liaison <strong>{kpis.merchant?.linkRate ?? 0}%</strong></div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-3">
            <div className="text-[11px] text-zinc-500 uppercase tracking-wide flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-emerald-600" /> Reliés à des factures
            </div>
            <div className="text-lg font-bold text-emerald-700">{fxof(kpis.merchant?.linked?.amount)}</div>
            <div className="text-[11px] text-zinc-500">{kpis.merchant?.linked?.count || 0} tx</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-3">
            <div className="text-[11px] text-zinc-500 uppercase tracking-wide flex items-center gap-1">
              <ShieldAlert className={`h-3 w-3 ${(kpis.merchant?.unlinked?.count || 0) > 0 ? 'text-rose-600' : 'text-zinc-400'}`} /> Merchants à traiter
            </div>
            <div className={`text-lg font-bold ${(kpis.merchant?.unlinked?.count || 0) > 0 ? 'text-rose-700' : 'text-zinc-500'}`}>
              {kpis.merchant?.unlinked?.count || 0}
            </div>
            <div className="text-[11px] text-zinc-500">{fxof(kpis.merchant?.unlinked?.amount)}</div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-3">
            <div className="text-[11px] text-zinc-500 uppercase tracking-wide flex items-center gap-1">
              <ShieldAlert className={`h-3 w-3 ${(kpis.agent?.unclassified?.count || 0) > 0 ? 'text-amber-600' : 'text-zinc-400'}`} /> Agents à classer
            </div>
            <div className={`text-lg font-bold ${(kpis.agent?.unclassified?.count || 0) > 0 ? 'text-amber-700' : 'text-zinc-500'}`}>
              {kpis.agent?.unclassified?.count || 0}
            </div>
            <div className="text-[11px] text-zinc-500">Taux classification {kpis.agent?.classificationRate ?? 0}%</div>
          </div>
        </div>
      )}

      {loading && <div className="text-xs text-zinc-500"><Loader2 className="inline h-3 w-3 animate-spin" /> Chargement des entrées...</div>}

      {/* Merchant Payments */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="wave-merchant-section">
        <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-semibold">Encaissements Merchants (règlement clients)</span>
            <Badge variant="outline" className="text-[10px]">{merchantList.total || 0}</Badge>
          </div>
        </div>
        {merchantSelection.length > 0 && (
          <div className="px-3 pt-3">
            <BulkActionBar
              count={merchantSelection.length}
              total={pendingMerchantIds.length}
              label="encaissement(s)"
              busy={bulkBusy}
              onSelectAll={() => setMerchantSelection(pendingMerchantIds)}
              onClear={() => setMerchantSelection([])}
              testid="wave-merchant-bulk-bar"
              actions={[
                { key: 'unidentified', label: 'Marquer non identifié',
                  className: 'border-zinc-300 text-zinc-700 hover:bg-zinc-100',
                  onClick: () => openBulk({
                    kind: 'unidentified', ids: merchantSelection, label: 'Non identifié',
                  }) },
              ]}
            />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="px-3 py-2 text-left w-8">
                  <input
                    type="checkbox" className="h-3.5 w-3.5 cursor-pointer accent-emerald-600"
                    checked={merchantSelection.length > 0 && merchantSelection.length === pendingMerchantIds.length}
                    onChange={(e) => setMerchantSelection(e.target.checked ? pendingMerchantIds : [])}
                    disabled={pendingMerchantIds.length === 0}
                    title="Sélectionner tous les encaissements à traiter"
                    data-testid="wave-merchant-select-all"
                  />
                </th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Réf. Wave</th>
                <th className="px-3 py-2 text-left">Contrepartie</th>
                <th className="px-3 py-2 text-right">Montant</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-left">Facture(s) liée(s)</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {merchantSorted.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-400">Aucun encaissement merchant sur la période.</td></tr>
              )}
              {merchantSorted.map(tx => {
                const isLinked = (tx.linkedInvoiceIds?.length || 0) > 0;
                const isUnidentified = tx.depositCategory === 'unknown';
                const selectable = !isLinked && !isUnidentified;
                return (
                  <tr key={tx.id}
                      className={`border-t border-zinc-100 hover:bg-emerald-50/30 ${merchantSelection.includes(tx.id) ? 'bg-emerald-50/60' : ''}`}
                      data-testid={`wave-merchant-row-${tx.id}`}>
                    <td className="px-3 py-2">
                      {selectable && (
                        <input
                          type="checkbox" className="h-3.5 w-3.5 cursor-pointer accent-emerald-600"
                          checked={merchantSelection.includes(tx.id)}
                          onChange={(e) => setMerchantSelection(prev => (
                            e.target.checked ? [...prev, tx.id] : prev.filter(id => id !== tx.id)
                          ))}
                          data-testid={`wave-merchant-select-${tx.id}`}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(tx.date)}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">{tx.waveRef}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{tx.beneficiaryName || '—'}</div>
                      <div className="text-[10px] text-zinc-500">{tx.beneficiaryPhone || ''}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fxof(tx.amountSent)}</td>
                    <td className="px-3 py-2">
                      {isLinked && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"><CheckCircle2 className="h-2.5 w-2.5 mr-1" />Rapproché</Badge>}
                      {!isLinked && isUnidentified && <Badge className="bg-zinc-100 text-zinc-700 border-zinc-200 text-[10px]">Non identifié</Badge>}
                      {!isLinked && !isUnidentified && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]"><ShieldAlert className="h-2.5 w-2.5 mr-1" />À traiter</Badge>}
                    </td>
                    <td className="px-3 py-2">
                      {tx.linkedInvoices?.length > 0 ? (
                        <div className="space-y-0.5">
                          {tx.linkedInvoices.map(inv => (
                            <div key={inv.id} className="text-[11px]"><span className="font-mono">{inv.invoiceNumber}</span> <span className="text-zinc-500">— {inv.clientName}</span></div>
                          ))}
                        </div>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {!isLinked && !isUnidentified && (
                        <>
                          <Button size="sm" variant="outline" className="h-7 text-[11px] mr-1"
                            onClick={() => setLinkTx(tx)} data-testid={`wave-merchant-link-btn-${tx.id}`}>
                            <Building2 className="h-3 w-3 mr-1" /> Lier
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-zinc-600"
                            onClick={() => setReasonModal({
                              tx, action: 'mark_unidentified',
                              title: 'Marquer non identifié',
                              description: `Aucun client trouvé pour ce paiement de ${fxof(tx.amountSent)} ?`,
                              minChars: 10,
                            })}
                            data-testid={`wave-merchant-unident-btn-${tx.id}`}>
                            Marquer non identifié
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Agent Transactions */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="wave-agent-section">
        <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowDownRight className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold">Versements Agent (à classer manuellement)</span>
            <Badge variant="outline" className="text-[10px]">{agentList.total || 0}</Badge>
          </div>
        </div>
        {agentSelection.length > 0 && (
          <div className="px-3 pt-3">
            <BulkActionBar
              count={agentSelection.length}
              total={unclassifiedAgentIds.length}
              label="versement(s)"
              busy={bulkBusy}
              onSelectAll={() => setAgentSelection(unclassifiedAgentIds)}
              onClear={() => setAgentSelection([])}
              testid="wave-agent-bulk-bar"
              actions={[
                { key: 'client', label: 'Client', className: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50',
                  onClick: () => bulkClassify('client_payment', 'Règlement client') },
                { key: 'deposit', label: 'Dépôt', className: 'border-sky-300 text-sky-700 hover:bg-sky-50',
                  onClick: () => bulkClassify('cash_deposit', 'Dépôt espèces') },
                { key: 'internal', label: 'Interne', className: 'border-violet-300 text-violet-700 hover:bg-violet-50',
                  onClick: () => bulkClassify('internal_transfer', 'Transfert interne') },
              ]}
            />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="px-3 py-2 text-left w-8">
                  <input
                    type="checkbox" className="h-3.5 w-3.5 cursor-pointer accent-emerald-600"
                    checked={agentSelection.length > 0 && agentSelection.length === unclassifiedAgentIds.length}
                    onChange={(e) => setAgentSelection(e.target.checked ? unclassifiedAgentIds : [])}
                    disabled={unclassifiedAgentIds.length === 0}
                    title="Sélectionner tous les versements à classer"
                    data-testid="wave-agent-select-all"
                  />
                </th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Réf. Wave</th>
                <th className="px-3 py-2 text-left">Contrepartie</th>
                <th className="px-3 py-2 text-right">Montant</th>
                <th className="px-3 py-2 text-left">Classification</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agentSorted.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-400">Aucun agent_transaction sur la période.</td></tr>
              )}
              {agentSorted.map(tx => {
                const cat = tx.depositCategory;
                const meta = cat ? CATEGORY_LABELS[cat] : null;
                const isUnclassified = !cat || cat === 'unknown';
                return (
                  <tr key={tx.id}
                      className={`border-t border-zinc-100 hover:bg-amber-50/30 ${agentSelection.includes(tx.id) ? 'bg-emerald-50/60' : ''}`}
                      data-testid={`wave-agent-row-${tx.id}`}>
                    <td className="px-3 py-2">
                      {isUnclassified && (
                        <input
                          type="checkbox" className="h-3.5 w-3.5 cursor-pointer accent-emerald-600"
                          checked={agentSelection.includes(tx.id)}
                          onChange={(e) => setAgentSelection(prev => (
                            e.target.checked ? [...prev, tx.id] : prev.filter(id => id !== tx.id)
                          ))}
                          data-testid={`wave-agent-select-${tx.id}`}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(tx.date)}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">{tx.waveRef}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{tx.beneficiaryName || '—'}</div>
                      <div className="text-[10px] text-zinc-500">{tx.beneficiaryPhone || tx.userName || ''}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-amber-700">{fxof(tx.amountSent)}</td>
                    <td className="px-3 py-2">
                      {meta ? (
                        <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200"><ShieldAlert className="h-2.5 w-2.5 mr-1" />À classer</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {isUnclassified ? (
                        <>
                          <Button size="sm" variant="outline" className="h-7 text-[11px] mr-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => setLinkTx(tx)}
                            data-testid={`wave-agent-classify-client-${tx.id}`}
                            title="Règlement client → lier à une facture">
                            <UserCheck className="h-3 w-3 mr-1" /> Client
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[11px] mr-1 border-sky-300 text-sky-700 hover:bg-sky-50"
                            onClick={() => setReasonModal({
                              tx, action: 'classify', category: 'cash_deposit',
                              title: 'Classer comme dépôt d\'espèces',
                              description: `Confirmer que ce versement de ${fxof(tx.amountSent)} est un dépôt cash agent Wave ?`,
                              minChars: 10,
                            })}
                            data-testid={`wave-agent-classify-cash-${tx.id}`}>
                            <Coins className="h-3 w-3 mr-1" /> Dépôt
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[11px] mr-1 border-violet-300 text-violet-700 hover:bg-violet-50"
                            onClick={() => setReasonModal({
                              tx, action: 'classify', category: 'internal_transfer',
                              title: 'Classer comme virement interne',
                              description: `Confirmer que ce versement de ${fxof(tx.amountSent)} est un renflouement caisse / virement interne ?`,
                              minChars: 10,
                            })}
                            data-testid={`wave-agent-classify-internal-${tx.id}`}>
                            <Send className="h-3 w-3 mr-1" /> Interne
                          </Button>
                        </>
                      ) : (
                        <span className="text-[10px] text-zinc-400">Classé le {fmtDate(tx.depositClassifiedAt)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <LinkInvoiceModal open={!!linkTx} onClose={() => setLinkTx(null)} tx={linkTx} onDone={fetchAll} />
      <ReasonModal
        open={!!reasonModal}
        onClose={() => setReasonModal(null)}
        title={reasonModal?.title}
        description={reasonModal?.description}
        minChars={reasonModal?.minChars || 10}
        onSubmit={submitReason}
        submitting={reasonModal?.submitting}
      />
      <ReasonModal
        open={!!bulkModal}
        onClose={() => setBulkModal(null)}
        title={`${bulkModal?.label || 'Action'} — ${bulkModal?.ids?.length || 0} ligne(s)`}
        description={"Le motif saisi ici est appliqué à chaque ligne du lot et conservé dans l'audit."}
        minChars={10}
        onSubmit={submitBulk}
        submitting={bulkBusy}
      />
    </div>
  );
}

export default WaveInflowsPanel;
